import { NavLink } from 'react-router-dom'
import { Crown } from 'iconoir-react'
import { snakeCase } from 'lodash'
import type { ReactElement, ReactNode } from 'react'
import { OverflowText } from 'app/components/shared/styled/OverflowText'
import ChipV2 from 'app/components/sidebar/ChipV2'
import { LockIcon } from 'app/components/contacts/LockIcon'
import type { useHomepage } from 'app/hooks/useHomepage'
import { cn } from 'app/lib/utils'
import { notifySuccess } from 'app/services/notify.client'
import type { List } from 'app/generated/graphql'
import type { CustomPageJson } from 'app/types/entities'
import { titleCase } from 'app/utils/titleCase'
import { roundCount } from 'lib/formatCount'
import type { NavItemType } from 'types/Sidebar'

export function NavRow({
  item,
  homepage,
  customPage,
  list,
  onEditList,
  onSettings,
  marker,
  muted,
}: {
  item: NavItemType
  homepage: ReturnType<typeof useHomepage>
  customPage?: CustomPageJson
  /** A contact list built from the List API: a filter list's rule is edited through it. */
  list?: List
  onEditList?: () => void
  onSettings?: () => void
  marker?: ReactNode
  /** A quieter icon for rows that repeat the same glyph, like the lists under one record type. */
  muted?: boolean
}): ReactElement {
  const Icon = item.icon
  const icon = Icon && (
    <span
      className={cn(
        `flex h-9 w-9 shrink-0 items-center justify-center`,
        muted ? `text-minimal [&_svg]:size-4` : `text-default`,
      )}
    >
      <Icon aria-hidden="true" />
    </span>
  )

  if (item.isLocked) {
    return (
      <div aria-label={item.name} className="flex h-9 items-center gap-x-2 pl-1.5 pr-2">
        <span className="opacity-50">{icon}</span>
        <span className="min-w-0 grow truncate text-heading-xs text-strong opacity-50">{titleCase(item.name)}</span>
        <LockIcon message="This content is unavailable because you don't have any locations with an active PMS integration" />
      </div>
    )
  }

  return (
    <NavLink
      to={item.href ?? `#`}
      aria-label={item.name}
      x-nav-selector={snakeCase(item.name)}
      className={cn(
        `group/nav-item relative flex h-9 items-center gap-x-2 pl-1.5 pr-2`,
        item.active ? `rounded-md bg-(--fg-strong)/6` : `hover:rounded-md hover:bg-(--fg-strong)/4`,
      )}
    >
      {icon}
      <span className="flex min-w-0 grow items-center gap-x-2">
        <OverflowText
          hasTooltip
          placement="right"
          content={titleCase(item.name)}
          className="min-w-0 cursor-pointer! text-left text-heading-xs text-strong"
        />
        {marker}
        {item.superUserOnly && (
          <span className="shrink-0 rounded-sm bg-amber-500/8 p-1">
            <Crown width={14} height={14} className="text-amber-800" />
          </span>
        )}
      </span>
      <ChipV2
        total={roundCount(item.count ?? 0)}
        hide={item.count == null}
        countColor={item.countColor}
        href={item.href}
        customPage={customPage}
        list={list}
        onEditList={onEditList}
        onSettings={onSettings}
        isDefault={homepage.href === item.href}
        lockedDefault={homepage.isDefaultHomepage && homepage.href === item.href}
        isActive={item.active}
        onSetAsDefault={async () => {
          const isCurrentlyPinned = homepage.explicitHref === item.href
          await homepage.setHomepage(isCurrentlyPinned ? undefined : item.href)
          notifySuccess(isCurrentlyPinned ? `Homepage reset` : `Homepage set to ${titleCase(item.name)}`)
        }}
      />
    </NavLink>
  )
}
